// "Form submissions" inbox.
//
// The author-facing half of the built-in submissions store. Before it,
// a page form with no hand-wired `call_api` step submitted NOWHERE — so the
// visitor saw a success state and the enquiry was gone. The store gives every
// form a real destination; this is where the author actually reads it.
//
// Two deliberate behaviours:
//   • Suspected spam is SHOWN, behind a toggle, with the guard that flagged
//     it. Silently hiding it would make a mis-tuned honeypot indistinguishable
//     from "nobody contacted us".
//   • "Not spam" is one click and clears the stale reason, so recovering a
//     false positive is trivial rather than a support ticket.

import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, Inbox, Loader2, RefreshCw, Trash2 } from 'lucide-react'
import {
  deleteFormSubmission, listFormSubmissions, updateFormSubmission,
  type FormSubmissionRow,
} from '@/api/website'
import { useAuth } from '@/auth/AuthContext'

interface Props {
  project: string
  /**
   * Project OWNER's user id — submissions are keyed by it, not by the
   * viewer, so a shared project's collaborators read the same inbox.
   * Defaults to the signed-in user, which is correct for an owned project.
   */
  userId?: string
  /** Scope to one page. Omit for every form in the project. */
  pageObjName?: string
}

// Plain English for each guard, so a flagged enquiry says what actually
// happened rather than naming an internal constant.
const SPAM_REASON_HELP: Record<string, string> = {
  honeypot: 'A hidden field that no visitor can see or reach was filled in — only an automated filler does that.',
  too_fast: 'Submitted faster than a person could have read and typed the form.',
  rate_limited: 'Too many submissions came from this address in an hour. The weakest signal of the set — a shared office connection can trip it.',
  captcha_missing: 'No reCAPTCHA token was sent. Either a script that never ran the check, or a visitor whose browser blocked Google\u2019s script.',
  captcha_failed: 'Google refused the reCAPTCHA token — expired, already spent, or minted for a different page.',
  captcha_low_score: 'Google accepted the token but scored the interaction below this project\u2019s threshold. The score is shown beside this.',
}

export default function FormSubmissionsPanel({ userId, project, pageObjName }: Props) {
  const { user } = useAuth()
  const ownerId = userId || user?.user_id || ''
  const [rows, setRows] = useState<FormSubmissionRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [includeSpam, setIncludeSpam] = useState(false)
  const [expanded, setExpanded] = useState<number | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await listFormSubmissions(ownerId, project, {
        pageObjName, includeSpam, limit: 200,
      })
      setRows(res.submissions)
      setTotal(res.total)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load submissions')
    } finally {
      setLoading(false)
    }
  }, [ownerId, project, pageObjName, includeSpam])

  useEffect(() => {
    // Without an owner id there is nothing to query; say so rather than
    // firing a request that will 404.
    if (!ownerId) { setLoading(false); setError('Sign in to read submissions.'); return }
    void load()
  }, [load, ownerId])

  // Optimistic: the inbox is a read-mostly list, and waiting for a round trip
  // to grey out a row makes marking a batch as read feel broken.
  const patch = async (id: number, body: Parameters<typeof updateFormSubmission>[3]) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...body } as FormSubmissionRow : r)))
    try {
      await updateFormSubmission(ownerId, project, id, body)
    } catch {
      void load()   // reconcile with the server if the write lost
    }
  }

  const remove = async (id: number) => {
    setRows((prev) => prev.filter((r) => r.id !== id))
    setTotal((t) => Math.max(0, t - 1))
    try {
      await deleteFormSubmission(ownerId, project, id)
    } catch {
      void load()
    }
  }

  const unread = rows.filter((r) => !r.is_read && !r.is_spam).length

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Inbox size={15} />
        <strong style={{ fontSize: 14 }}>Form submissions</strong>
        {unread > 0 && (
          <span style={{
            fontSize: 13, fontWeight: 700, padding: '1px 7px', borderRadius: 999,
            background: 'var(--color-accent)', color: '#fff',
          }}>{unread} new</span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--color-text-secondary)' }}>
          {total} total
        </span>
        <button onClick={() => void load()} className="btn btn-ghost btn-sm" title="Refresh">
          <RefreshCw size={13} />
        </button>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
        <input type="checkbox" checked={includeSpam} onChange={(e) => setIncludeSpam(e.target.checked)} />
        Show suspected spam
      </label>

      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--color-text-secondary)' }}>
          <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Loading…
        </div>
      )}

      {error && (
        <div style={{ fontSize: 13, color: 'var(--color-error)' }}>{error}</div>
      )}

      {!loading && !error && rows.length === 0 && (
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: 'var(--color-text-secondary)' }}>
          No submissions yet. Any <code>form</code>, <code>contact form</code> or{' '}
          <code>newsletter</code> block on this page stores here by default —
          set a different destination on the block to send them elsewhere.
        </p>
      )}

      {rows.map((r) => {
        const open = expanded === r.id
        return (
          <div
            key={r.id}
            style={{
              border: '1px solid var(--color-border)', borderRadius: 8, padding: 10,
              background: r.is_read ? 'transparent' : 'var(--color-bg-secondary)',
              opacity: r.is_spam ? 0.65 : 1,
              display: 'flex', flexDirection: 'column', gap: 6,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                onClick={() => {
                  setExpanded(open ? null : r.id)
                  if (!r.is_read) void patch(r.id, { is_read: true })
                }}
                style={{
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  fontSize: 13.5, fontWeight: r.is_read ? 500 : 700, color: 'var(--color-text)',
                  textAlign: 'left', flex: 1, minWidth: 0,
                }}
              >
                {r.form_label || r.page_obj_name}
                <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)' }}>
                  {' — '}{summarise(r.payload)}
                </span>
              </button>
              {r.is_spam && (
                <span
                  title={SPAM_REASON_HELP[r.spam_reason || ''] || `Flagged by the ${r.spam_reason} guard`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 13, color: 'var(--color-warning, #b45309)' }}
                >
                  <AlertTriangle size={11} /> {r.spam_reason}
                </span>
              )}
              {/* The score, on ACCEPTED submissions too. A flagged row reading
                  0.1 and a flagged row reading 0.9 mean opposite things, and
                  seeing the numbers on real enquiries is the only way to tell
                  whether a threshold is set right. */}
              {typeof r.captcha_score === 'number' && (
                <span
                  title={`Google scored this submission ${r.captcha_score.toFixed(1)} out of 1.0 — higher is more likely to be a person.`}
                  style={{
                    fontSize: 13, fontVariantNumeric: 'tabular-nums',
                    color: r.captcha_score < 0.5
                      ? 'var(--color-warning, #b45309)'
                      : 'var(--color-text-secondary)',
                  }}
                >
                  score {r.captcha_score.toFixed(1)}
                </span>
              )}
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                {formatWhen(r.created_at)}
              </span>
            </div>

            {open && (
              <>
                <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 10px', fontSize: 13 }}>
                  {Object.entries(r.payload || {}).map(([k, v]) => (
                    <div key={k} style={{ display: 'contents' }}>
                      <dt style={{ color: 'var(--color-text-secondary)', fontWeight: 600 }}>{k}</dt>
                      <dd style={{ margin: 0, wordBreak: 'break-word' }}>{String(v ?? '')}</dd>
                    </div>
                  ))}
                </dl>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <button
                    onClick={() => void patch(r.id, { is_spam: !r.is_spam })}
                    className="btn btn-ghost btn-sm"
                    style={{ fontSize: 13 }}
                  >
                    {r.is_spam ? 'Not spam' : 'Mark as spam'}
                  </button>
                  <button
                    onClick={() => void patch(r.id, { is_read: !r.is_read })}
                    className="btn btn-ghost btn-sm"
                    style={{ fontSize: 13 }}
                  >
                    {r.is_read ? 'Mark unread' : 'Mark read'}
                  </button>
                  <button
                    onClick={() => void remove(r.id)}
                    className="btn btn-ghost btn-sm"
                    style={{ marginLeft: 'auto', color: 'var(--color-error)' }}
                    title="Delete permanently"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </>
            )}
          </div>
        )
      })}
    </div>
  )
}

/** First couple of field values, for the collapsed row. */
function summarise(payload: Record<string, unknown>): string {
  const parts = Object.values(payload || {})
    .map((v) => String(v ?? '').trim())
    .filter(Boolean)
  const text = parts.slice(0, 2).join(' · ')
  return text.length > 70 ? `${text.slice(0, 70)}…` : (text || 'empty')
}

function formatWhen(iso?: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const mins = Math.round((Date.now() - d.getTime()) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`
  return d.toLocaleDateString()
}
